import * as React from "react"
import type { ViewKey } from "@digits-graphql/frontend/graphql-bearer"
import { useReadLegalEntityMetadataQuery } from "@digits-graphql/frontend/graphql-bearer"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import type FrontendSession from "src/frontend/session"

export function useTransactionTimestamps(skip?: boolean) {
  const viewKey = useViewVersion()
  const { hasAccessToAspect } = useSession<FrontendSession>()
  const allowFutureDates = hasAccessToAspect?.(AspectCode.FutureDatedTransactions) ?? false
  const skipQuery = useSkipMetadataQuery(viewKey, skip)

  const { data, loading, error } = useReadLegalEntityMetadataQuery({
    variables: {
      viewKey,
    },
    skip: skipQuery,
  })

  return React.useMemo(() => {
    const firstTransactionTimestamp = data?.readLegalEntityMetadata?.firstTransactionTimestamp || 0
    const latestTransactionTimestamp =
      data?.readLegalEntityMetadata?.latestTransactionTimestamp || 0
    const firstDate = dayjs.unix(firstTransactionTimestamp)
    const latestDate = dayjs.unix(latestTransactionTimestamp)

    const todayEnd = dayjs.utc().endOf("day").unix()
    let maxNavigableTimestamp: number
    if (allowFutureDates && latestTransactionTimestamp) {
      maxNavigableTimestamp = Math.max(latestTransactionTimestamp, todayEnd)
    } else if (allowFutureDates && loading) {
      // While metadata is loading, use a generous upper bound to avoid prematurely
      // capping future-dated URLs before the real boundary is known.
      maxNavigableTimestamp = dayjs.utc().add(10, "year").endOf("year").unix()
    } else {
      maxNavigableTimestamp = todayEnd
    }
    const maxNavigableYear = dayjs.utc(maxNavigableTimestamp * 1000).year()

    return {
      loading,
      metadataLoaded: data !== undefined || !!error,
      error,
      firstTransactionTimestamp,
      firstTransactionMonth: firstDate.month(),
      firstTransactionYear: firstDate.year(),
      latestTransactionTimestamp,
      latestTransactionMonth: latestDate.month(),
      latestTransactionYear: latestDate.year(),
      maxNavigableTimestamp,
      maxNavigableYear,
    }
  }, [data, error, loading, allowFutureDates])
}

function useSkipMetadataQuery(viewKey: ViewKey | undefined, skip?: boolean): boolean {
  const { location } = useRouter()
  const session = useSession<FrontendSession>()
  const { isSharingContextActive, currentLegalEntity } = session

  const viewKeyLegalEntitySlug = viewKey?.legalEntityId
    ? session.findLegalEntityById(viewKey.legalEntityId)?.slug
    : undefined

  // Parse leSlug from the pathname instead of useRouteMatch().params.
  // useRouteMatch() only sees params from the nearest <Route> ancestor, and
  // TimeContextURLProvider sits above any Route that matches /@:leSlug in the
  // layered router tree, so params.leSlug is always undefined there. Parsing
  // the pathname directly works consistently at every level.
  const leSlug = leSlugFromPathname(location.pathname)

  return shouldSkipMetadataQuery({
    leSlug,
    viewKeyLegalEntitySlug,
    viewKey,
    hasDashboardAccess: !!currentLegalEntity?.hasDashboardAccess(),
    isSharingContextActive,
    callerSkip: !!skip,
  })
}

const LE_SLUG_PATTERN = /^\/@([^/]+)/

function leSlugFromPathname(pathname: string): string | undefined {
  return LE_SLUG_PATTERN.exec(pathname)?.[1]
}

interface SkipMetadataQueryInput {
  leSlug: string | undefined
  viewKeyLegalEntitySlug: string | undefined
  viewKey: ViewKey | undefined
  hasDashboardAccess: boolean
  isSharingContextActive: boolean
  callerSkip: boolean
}

function shouldSkipMetadataQuery({
  leSlug,
  viewKeyLegalEntitySlug,
  viewKey,
  hasDashboardAccess,
  isSharingContextActive,
  callerSkip,
}: SkipMetadataQueryInput): boolean {
  if (callerSkip || isSharingContextActive || !hasDashboardAccess) return true
  if (!leSlug || leSlug !== viewKeyLegalEntitySlug) return true
  if (!viewKey?.legalEntityId || !viewKey?.viewVersion) return true
  return false
}
